import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTasksList } from './hooks'
import { InputRef, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Task } from './types'
import CIcon from '@coreui/icons-react'
import { cilZoom } from '@coreui/icons'
import { path, role } from '../../../../libs'
import { ChangeTaskModal } from '../widgets/changeTaskModal/component'
import { ClientTaskModal } from '../widgets/clientTaskModal/component'
import { TasksTypesNames } from '../consts'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { Link } from 'react-router-dom'
import { FilterValue } from 'antd/es/table/interface'
import { tasksTypesListMounted } from '../widgets/changeTaskModal/model'
import { useTasksTypesList } from '../widgets/changeTaskModal/hooks'
import { Protect } from '../../../../features/session'

export const TasksList: React.FC = () => {
  const [isVisibleModal, setIsVisibleModal] = useState(false)
  const [visibleTaskModal, setVisibleTaskModal] = useState<{
    isVisible: boolean
    data: Task | null
  }>({
    isVisible: false,
    data: null,
  })

  const { tasks, loading, onChangeTasksList, totalCountOfTasks } =
    useTasksList()
  const { tasksTypes } = useTasksTypesList()

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: ['check'],
  })

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }
    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    tasksTypesListMounted()
  }, [])

  useEffect(() => {
    onChangeTasksList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, filtersState, searchText])

  const taskTypeFilters = useMemo(
    () =>
      tasksTypes.map((item) => ({
        value: item.taskType,
        text: TasksTypesNames[item.taskType as keyof typeof TasksTypesNames],
      })),
    [tasksTypes],
  )

  const columns: ColumnsType<Task> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      ...getColumnSearchProps<Task>(
        'client.clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...getColumnSearchProps<Task>(
        'client.fullName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...getColumnSearchProps<Task>(
        'client.email',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Task',
      dataIndex: 'taskType',
      render: (item: string) =>
        TasksTypesNames[item as keyof typeof TasksTypesNames],
      filters: taskTypeFilters,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: [
        {
          text: 'new',
          value: 'check',
        },
        {
          text: 'rejected',
          value: 'declined',
        },
        {
          text: 'accepted',
          value: 'accepted',
        },
      ],
      filterMultiple: false,
      defaultFilteredValue: ['check'],
    },
    {
      title: 'Actions',
      fixed: 'right',
      render: (item: Task) => {
        return (
          <div>
            <CButton
              color="secondary"
              size="sm"
              onClick={() => {
                setVisibleTaskModal({
                  isVisible: true,
                  data: item,
                })
              }}
            >
              <CIcon icon={cilZoom} />
            </CButton>
          </div>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <Protect allow={[role.admin(), role.root()]}>
        <CButton
          color="warning"
          className="mb-2"
          onClick={() => setIsVisibleModal(true)}
        >
          Change tasks
        </CButton>
      </Protect>
      <Table
        dataSource={tasks}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="competionsId"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfTasks,
        }}
        onChange={(pagination, filters, sorter) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      />
      {isVisibleModal && (
        <ChangeTaskModal
          isVisible={isVisibleModal}
          onClose={() => setIsVisibleModal(false)}
        />
      )}
      {visibleTaskModal.isVisible && (
        <ClientTaskModal
          isVisible={visibleTaskModal.isVisible}
          onClose={() => setVisibleTaskModal({ isVisible: false, data: null })}
          data={visibleTaskModal.data}
        />
      )}
    </CCardBody>
  )
}
